/**
 * M3 Expressive Accessibility Service
 * 
 * Auto-detects and applies accessibility preferences per M3 guidelines:
 * - System contrast preferences (prefers-contrast)
 * - Reduced motion preferences (prefers-reduced-motion)
 * - Color scheme preferences (prefers-color-scheme)
 * - Screen reader detection
 * 
 * M3 Research insight: "Expressive design was shown to be more visually
 * appealing, intuitive, and easy to use for participants with varying
 * movement and visual abilities. Larger buttons, high-contrast visual
 * containment, and other key attributes of M3 Expressive designs make
 * interfaces better and more usable for everyone."
 * 
 * @see https://design.google/library/expressive-material-design-google-research
 */

// =============================================================================
// TYPES
// =============================================================================

export interface AccessibilityPreferences {
  prefersHighContrast: boolean;
  prefersReducedMotion: boolean;
  prefersDarkMode: boolean;
  isLargeTextEnabled: boolean;
  screenReaderActive: boolean;
}

export interface M3AccessibilityConfig {
  // Typography adjustments
  minTapTargetSize: number;  // M3 recommends 48dp minimum
  minTextSize: number;       // Minimum readable text size
  emphasisBoost: number;     // Additional font weight for high contrast
  
  // Motion adjustments
  useReducedMotion: boolean;
  maxAnimationDuration: number;
  
  // Color adjustments  
  contrastBoost: number;     // Multiplier for color contrast
  focusRingWidth: number;    // Focus indicator width
}

// =============================================================================
// DEFAULT CONFIGURATIONS
// =============================================================================

const DEFAULT_CONFIG: M3AccessibilityConfig = {
  minTapTargetSize: 48,
  minTextSize: 14,
  emphasisBoost: 0,
  useReducedMotion: false,
  maxAnimationDuration: 1000,
  contrastBoost: 1.0,
  focusRingWidth: 2,
};

const HIGH_CONTRAST_CONFIG: M3AccessibilityConfig = {
  minTapTargetSize: 56,      // Larger targets
  minTextSize: 16,           // Larger minimum text
  emphasisBoost: 100,        // Max grade boost
  useReducedMotion: false,
  maxAnimationDuration: 1000,
  contrastBoost: 1.3,        // 30% contrast boost
  focusRingWidth: 3,         // Thicker focus rings
};

const REDUCED_MOTION_CONFIG: Partial<M3AccessibilityConfig> = {
  useReducedMotion: true,
  maxAnimationDuration: 0,   // Disable animations
};

// =============================================================================
// ACCESSIBILITY SERVICE CLASS
// =============================================================================

class AccessibilityService {
  private preferences: AccessibilityPreferences;
  private config: M3AccessibilityConfig;
  private listeners: Set<(prefs: AccessibilityPreferences) => void> = new Set();
  private mediaQueryListeners: Map<MediaQueryList, () => void> = new Map();
  
  constructor() {
    this.preferences = this.detectPreferences();
    this.config = this.computeConfig();
    this.setupMediaQueryListeners();
    this.applyAccessibilityClasses();
  }
  
  // ===========================================================================
  // DETECTION
  // ===========================================================================
  
  /**
   * Detect system accessibility preferences
   */
  private detectPreferences(): AccessibilityPreferences {
    if (typeof window === 'undefined') {
      return {
        prefersHighContrast: false,
        prefersReducedMotion: false,
        prefersDarkMode: false,
        isLargeTextEnabled: false,
        screenReaderActive: false,
      };
    }
    
    return {
      prefersHighContrast: this.checkHighContrast(),
      prefersReducedMotion: this.checkReducedMotion(),
      prefersDarkMode: this.checkDarkMode(),
      isLargeTextEnabled: this.checkLargeText(),
      screenReaderActive: this.checkScreenReader(),
    };
  }
  
  private checkHighContrast(): boolean {
    // Check for forced-colors (Windows High Contrast Mode)
    if (window.matchMedia('(forced-colors: active)').matches) {
      return true;
    }
    // Check for prefers-contrast
    if (window.matchMedia('(prefers-contrast: more)').matches) {
      return true;
    }
    return false;
  }
  
  private checkReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  
  private checkDarkMode(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  
  private checkLargeText(): boolean {
    // Check if user has large text/zoom enabled
    // Default browser font-size is 16px
    const fontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    return fontSize > 18;
  }
  
  private checkScreenReader(): boolean {
    // Heuristic: check for common screen reader indicators
    // Note: This is not 100% reliable, but helps with some optimizations
    const html = document.documentElement;
    return (
      html.getAttribute('data-whatintent') === 'keyboard' ||
      html.classList.contains('js-focus-visible') ||
      // NVDA/JAWS often set this
      document.body.getAttribute('aria-busy') === 'true'
    );
  }
  
  // ===========================================================================
  // CONFIGURATION
  // ===========================================================================
  
  /**
   * Compute optimal configuration based on detected preferences
   */
  private computeConfig(): M3AccessibilityConfig {
    let config = { ...DEFAULT_CONFIG };
    
    if (this.preferences.prefersHighContrast) {
      config = { ...config, ...HIGH_CONTRAST_CONFIG };
    }
    
    if (this.preferences.prefersReducedMotion) {
      config = { ...config, ...REDUCED_MOTION_CONFIG };
    }
    
    if (this.preferences.isLargeTextEnabled) {
      config.minTextSize = Math.max(config.minTextSize, 18);
      config.minTapTargetSize = Math.max(config.minTapTargetSize, 56);
    }
    
    return config;
  }
  
  // ===========================================================================
  // MEDIA QUERY LISTENERS
  // ===========================================================================
  
  /**
   * Set up listeners for preference changes
   */
  private setupMediaQueryListeners(): void {
    if (typeof window === 'undefined') return;
    
    const queries = [
      { query: '(prefers-contrast: more)', key: 'prefersHighContrast' },
      { query: '(forced-colors: active)', key: 'prefersHighContrast' },
      { query: '(prefers-reduced-motion: reduce)', key: 'prefersReducedMotion' },
      { query: '(prefers-color-scheme: dark)', key: 'prefersDarkMode' },
    ];
    
    queries.forEach(({ query }) => {
      const mql = window.matchMedia(query);
      const handler = () => {
        this.preferences = this.detectPreferences();
        this.config = this.computeConfig();
        this.applyAccessibilityClasses();
        this.notifyListeners();
      };
      
      // Modern browsers
      if (mql.addEventListener) {
        mql.addEventListener('change', handler);
      } else {
        // Legacy support
        mql.addListener(handler);
      }
      
      this.mediaQueryListeners.set(mql, handler);
    });
  }
  
  // ===========================================================================
  // DOM UPDATES
  // ===========================================================================
  
  /**
   * Apply CSS classes to document root based on preferences
   */
  private applyAccessibilityClasses(): void {
    if (typeof document === 'undefined') return;
    
    const root = document.documentElement;
    const classList = root.classList;
    
    // High contrast
    if (this.preferences.prefersHighContrast) {
      classList.add('m3-high-contrast');
      root.style.setProperty('--contrast-boost', String(this.config.contrastBoost));
      root.style.setProperty('--focus-ring-width', `${this.config.focusRingWidth}px`);
      root.style.setProperty('--min-tap-target', `${this.config.minTapTargetSize}px`);
      // Boost font grade for high contrast
      root.style.setProperty('--font-grade-boost', String(this.config.emphasisBoost));
    } else {
      classList.remove('m3-high-contrast');
      root.style.setProperty('--contrast-boost', '1');
      root.style.setProperty('--focus-ring-width', '2px');
      root.style.setProperty('--min-tap-target', '48px');
      root.style.setProperty('--font-grade-boost', '0');
    }
    
    // Reduced motion
    if (this.preferences.prefersReducedMotion) {
      classList.add('m3-reduced-motion');
      root.style.setProperty('--motion-duration-multiplier', '0');
    } else {
      classList.remove('m3-reduced-motion');
      root.style.setProperty('--motion-duration-multiplier', '1');
    }
    
    // Large text
    if (this.preferences.isLargeTextEnabled) {
      classList.add('m3-large-text');
      root.style.setProperty('--min-text-size', `${this.config.minTextSize}px`);
    } else {
      classList.remove('m3-large-text');
      root.style.setProperty('--min-text-size', '14px');
    }
  }
  
  // ===========================================================================
  // SUBSCRIPTION
  // ===========================================================================
  
  /**
   * Subscribe to preference changes
   */
  subscribe(callback: (prefs: AccessibilityPreferences) => void): () => void {
    this.listeners.add(callback);
    // Immediately call with current prefs
    callback(this.preferences);
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }
  
  private notifyListeners(): void {
    this.listeners.forEach(callback => callback(this.preferences));
  }
  
  // ===========================================================================
  // PUBLIC API
  // ===========================================================================
  
  /**
   * Get current accessibility preferences
   */
  getPreferences(): AccessibilityPreferences {
    return { ...this.preferences };
  }
  
  /**
   * Get current configuration
   */
  getConfig(): M3AccessibilityConfig {
    return { ...this.config };
  }
  
  /**
   * Check if high contrast mode should be enabled
   * Combines system preference with manual override
   */
  shouldUseHighContrast(manualOverride?: boolean): boolean {
    if (manualOverride !== undefined) {
      return manualOverride;
    }
    return this.preferences.prefersHighContrast;
  }
  
  /**
   * Check if animations should be reduced
   */
  shouldReduceMotion(): boolean {
    return this.preferences.prefersReducedMotion;
  }
  
  /**
   * Get animation duration with reduced motion consideration
   */
  getAnimationDuration(baseDuration: number): number {
    if (this.preferences.prefersReducedMotion) {
      return 0;
    }
    return baseDuration;
  }
  
  /**
   * Get CSS transition with reduced motion consideration
   */
  getTransition(property: string, duration: number, easing: string): string {
    const actualDuration = this.getAnimationDuration(duration);
    return `${property} ${actualDuration}ms ${easing}`;
  }
  
  /**
   * Apply high contrast to a color (increase saturation and contrast)
   */
  enhanceColor(color: string): string {
    if (!this.preferences.prefersHighContrast) {
      return color;
    }
    
    // For hex colors, increase saturation
    if (color.startsWith('#')) {
      // Convert to HSL, boost saturation, convert back
      // Simplified: just return the color with a filter applied via CSS
      return color;
    }
    
    return color;
  }
  
  /**
   * Manual refresh of preferences (e.g., after user changes settings)
   */
  refresh(): void {
    this.preferences = this.detectPreferences();
    this.config = this.computeConfig();
    this.applyAccessibilityClasses();
    this.notifyListeners();
  }
  
  /**
   * Cleanup listeners
   */
  destroy(): void {
    this.mediaQueryListeners.forEach((handler, mql) => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handler);
      } else {
        mql.removeListener(handler);
      }
    });
    this.mediaQueryListeners.clear();
    this.listeners.clear();
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const accessibilityService = new AccessibilityService();

// =============================================================================
// REACT HOOK
// =============================================================================

import { useEffect, useState } from 'react';

/**
 * React hook for accessing accessibility preferences
 */
export function useAccessibility(): AccessibilityPreferences {
  const [prefs, setPrefs] = useState<AccessibilityPreferences>(
    accessibilityService.getPreferences()
  );
  
  useEffect(() => {
    return accessibilityService.subscribe(setPrefs);
  }, []);
  
  return prefs;
}

/**
 * React hook for M3 accessibility configuration
 */
export function useM3AccessibilityConfig(): M3AccessibilityConfig {
  const [config, setConfig] = useState<M3AccessibilityConfig>(
    accessibilityService.getConfig()
  );
  
  useEffect(() => {
    return accessibilityService.subscribe(() => {
      setConfig(accessibilityService.getConfig());
    });
  }, []);
  
  return config;
}

export default accessibilityService;
